// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A label component for displaying asset inventory dates.
 * This module provides a simple interface for rendering
 * inventory dates with appropriate formatting and empty
 * state handling. It supports both set and unset dates
 * with visual differentiation.
 *
 * Key features:
 * - Date formatting
 * - Empty state handling
 * - Visual state indication
 * - Consistent styling
 * - Compact display
 */
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';

/**
 * Props for configuring the AssetInventoryDateLabel component.
 *
 * @interface Props
 */
interface Props {
  /**
   * A date representing the inventory date of an asset.
   * Null indicates the date hasn't been set.
   */
  inventoryDate: DateTime | null;
}

/**
 * A label component for displaying inventory dates.
 * Renders the date in a consistent format or shows
 * an empty state message when no date is set. Uses
 * different text colors to indicate state.
 *
 * Features:
 * - Date formatting
 * - Empty state handling
 * - Visual state indication
 * - Typography consistency
 * - Compact display
 *
 * Usage example:
 * ```tsx
 * function AssetDetails({ inventoryDate }: Props) {
 *   return (
 *     <AssetInventoryDateLabel
 *       inventoryDate={inventoryDate}
 *     />
 *   );
 * }
 * ```
 */
export function AssetInventoryDateLabel(props: Props) {
  const color =
    props.inventoryDate === null ? 'text.secondary' : 'text.primary';

  const label =
    props.inventoryDate === null
      ? 'Not set'
      : props.inventoryDate.toLocaleString({ dateStyle: 'medium' });

  return (
    <Typography variant="body2" color={color}>
      {label}
    </Typography>
  );
}
